/**
 * iter-338i: BOQ Profitability Dialog.
 *
 * Owner/Admin/Staff-only tab that breaks down profit + margin per Legend
 * Category. Pulls from GET /api/boq-worksheets/{id}/profitability and lets
 * admins:
 *  - Retroactively attach a category to vendor payments that are missing one.
 *  - Export the table to Excel or PDF.
 */
import { useEffect, useState, useCallback, Fragment } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { TrendingUp, FileSpreadsheet, FileText, AlertTriangle, X, RefreshCw, ChevronDown, ChevronRight, Save, Unlink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtINR = (n) => `Rs ${Math.round(+n || 0).toLocaleString('en-IN')}`;

export function ProfitabilityDialog({ open, onOpenChange, worksheetId, worksheetNumber, legendCategories }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState({});
  // iter-338i-amend: per-category expand state + per-payment amount edit buffer + saving flags
  const [expanded, setExpanded] = useState({});
  const [amountDraft, setAmountDraft] = useState({});
  const [savingPayment, setSavingPayment] = useState({});
  // iter-338i-amend (test feedback): pull the FULL legend category list so the
  // Reassign dropdown can target any category — not just those already used in
  // this BOQ. Falls back to the prop list passed by BOQEditPage.
  const [allLegendCategories, setAllLegendCategories] = useState(null);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/boq-categories/main`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!cancelled) setAllLegendCategories(r.data || []);
      } catch {
        if (!cancelled) setAllLegendCategories(null);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);
  const effectiveLegendCategories = (allLegendCategories && allLegendCategories.length > 0)
    ? allLegendCategories.map((c) => ({ code: c.code, group: c.code, name: c.name, group_name: c.name }))
    : (legendCategories || []);

  const fetchData = useCallback(async () => {
    if (!worksheetId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/boq-worksheets/${worksheetId}/profitability`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load profitability');
    } finally {
      setLoading(false);
    }
  }, [worksheetId]);

  useEffect(() => {
    if (!open || !worksheetId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/boq-worksheets/${worksheetId}/profitability`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || 'Could not load profitability');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, worksheetId]);

  const assignCategory = async (paymentId, code) => {
    if (!code) return;
    setAssigning((p) => ({ ...p, [paymentId]: true }));
    try {
      const cat = (effectiveLegendCategories || []).find((c) => (c.code || c.group) === code);
      await axios.put(`${API}/vendor-payments/${paymentId}/assign-category`, {
        legend_category_code: code,
        legend_category_name: cat ? (cat.name || cat.group_name) : undefined,
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success(`Assigned to ${code}`);
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not assign category');
    } finally {
      setAssigning((p) => ({ ...p, [paymentId]: false }));
    }
  };

  // iter-338i-amend: PUT /api/vendor-payments/{id} to amend amount partial/full
  const amendAmount = async (paymentId) => {
    const next = parseFloat(amountDraft[paymentId]);
    if (!next || next <= 0) {
      toast.error('Enter a positive amount');
      return;
    }
    setSavingPayment((p) => ({ ...p, [paymentId]: true }));
    try {
      await axios.put(`${API}/vendor-payments/${paymentId}`, { amount: next }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      toast.success(`Amount updated to Rs ${next.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
      setAmountDraft((p) => { const c = { ...p }; delete c[paymentId]; return c; });
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Amend failed');
    } finally {
      setSavingPayment((p) => ({ ...p, [paymentId]: false }));
    }
  };

  // iter-338i-amend: delink — send empty code to clear category attribution
  const delinkPayment = async (paymentId) => {
    if (!window.confirm('Delink this payment from this category? It will move back to "Uncategorized" until reassigned.')) return;
    setSavingPayment((p) => ({ ...p, [paymentId]: true }));
    try {
      await axios.put(`${API}/vendor-payments/${paymentId}/assign-category`, {
        legend_category_code: '',
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success('Payment delinked');
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delink failed');
    } finally {
      setSavingPayment((p) => ({ ...p, [paymentId]: false }));
    }
  };

  // iter-338i-amend: reassign — same endpoint with new code
  const reassignPayment = async (paymentId, newCode) => {
    if (!newCode) return;
    setSavingPayment((p) => ({ ...p, [paymentId]: true }));
    try {
      const cat = (effectiveLegendCategories || []).find((c) => (c.code || c.group) === newCode);
      await axios.put(`${API}/vendor-payments/${paymentId}/assign-category`, {
        legend_category_code: newCode,
        legend_category_name: cat ? (cat.name || cat.group_name) : undefined,
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success(`Moved to ${newCode}`);
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reassign failed');
    } finally {
      setSavingPayment((p) => ({ ...p, [paymentId]: false }));
    }
  };

  const exportFile = async (fmt) => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(
        `${API}/boq-worksheets/${worksheetId}/profitability/export?format=${fmt}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' },
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `BOQ_${worksheetNumber || worksheetId}_Profitability.${fmt === 'pdf' ? 'pdf' : 'xlsx'}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    }
  };

  const rows = data?.rows || [];
  const totals = data?.totals || {};
  const uncategorized = data?.uncategorized_payments || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl !max-h-[92vh] flex flex-col overflow-hidden p-0" data-testid="profitability-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-stone-200 bg-emerald-50/50 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp size={20} className="text-emerald-700" />
            <span>Profitability — {worksheetNumber || 'BOQ'}</span>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Per-Legend-Category profit and margin breakdown for this BOQ. Expand a category row to amend, reassign, or delink linked vendor payments.
          </DialogDescription>
          <p className="text-xs text-stone-600 mt-1">
            Margins per <strong>Legend Category</strong>. Estimated cost/revenue come from BOQ line items; Actual Vendor Paid pulls from recorded vendor payments attributed to the category.
          </p>
        </DialogHeader>

        <div className="flex items-center justify-end gap-2 px-6 py-3 border-b border-stone-200 bg-white flex-shrink-0">
          <Button size="sm" variant="outline" onClick={fetchData} disabled={loading} data-testid="profitability-refresh-btn">
            <RefreshCw size={14} className="mr-1" />Refresh
          </Button>
          <Button size="sm" variant="outline" onClick={() => exportFile('excel')} data-testid="profitability-export-excel-btn">
            <FileSpreadsheet size={14} className="mr-1" />Excel
          </Button>
          <Button size="sm" variant="outline" onClick={() => exportFile('pdf')} data-testid="profitability-export-pdf-btn">
            <FileText size={14} className="mr-1" />PDF
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
          {loading && <p className="text-sm text-stone-500 text-center py-8">Loading…</p>}
          {!loading && uncategorized.length > 0 && (
            <div className="mb-4 rounded-lg border-2 border-amber-300 bg-amber-50 p-3" data-testid="profitability-uncategorized-banner">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={16} className="text-amber-700" />
                <strong className="text-sm text-amber-900">
                  {uncategorized.length} vendor payment(s) need a category assignment
                </strong>
              </div>
              <p className="text-xs text-amber-800 mb-2">
                Assign these payments to a Legend Category to include them in the profitability breakdown below.
              </p>
              <table className="w-full text-xs">
                <thead className="text-amber-900">
                  <tr className="border-b border-amber-200">
                    <th className="text-left py-1 px-1">Vendor</th>
                    <th className="text-right py-1 px-1">Amount</th>
                    <th className="text-left py-1 px-1">Date</th>
                    <th className="text-left py-1 px-1">Notes</th>
                    <th className="text-left py-1 px-1 w-48">Assign to Category</th>
                  </tr>
                </thead>
                <tbody>
                  {uncategorized.map((p) => (
                    <tr key={p.id} className="border-b border-amber-100" data-testid={`profitability-uncategorized-row-${p.id}`}>
                      <td className="py-1 px-1 text-stone-700">{p.vendor_name || '—'}</td>
                      <td className="py-1 px-1 text-right font-semibold text-stone-900">{fmtINR(p.amount)}</td>
                      <td className="py-1 px-1 text-stone-600">{p.payment_date ? new Date(p.payment_date).toLocaleDateString('en-IN') : '—'}</td>
                      <td className="py-1 px-1 text-stone-500 truncate max-w-[180px]">{p.notes || '—'}</td>
                      <td className="py-1 px-1">
                        <select
                          onChange={(e) => assignCategory(p.id, e.target.value)}
                          disabled={assigning[p.id]}
                          className="w-full h-7 text-xs border border-amber-300 rounded px-1 bg-white"
                          data-testid={`profitability-assign-${p.id}`}
                          defaultValue=""
                        >
                          <option value="">— pick —</option>
                          {(effectiveLegendCategories || []).map((c) => {
                            const code = c.code || c.group;
                            const name = c.name || c.group_name;
                            return <option key={code} value={code}>{code} — {name}</option>;
                          })}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && rows.length === 0 && uncategorized.length === 0 && (
            <p className="text-sm text-stone-500 text-center py-8">No items to compute profitability. Add line items + record vendor payments to see margins.</p>
          )}

          {rows.length > 0 && (
            <div className="overflow-x-auto" data-testid="profitability-table">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-stone-100 text-stone-700">
                  <tr>
                    <th rowSpan={2} className="px-2 py-2 text-left border border-stone-200 min-w-[180px]">Category</th>
                    <th rowSpan={2} className="px-2 py-2 text-center border border-stone-200">Items</th>
                    <th colSpan={4} className="px-2 py-2 text-center border border-stone-200 bg-blue-50 text-blue-800">Estimated</th>
                    <th colSpan={4} className="px-2 py-2 text-center border border-stone-200 bg-emerald-50 text-emerald-800">Actual</th>
                  </tr>
                  <tr className="text-[11px]">
                    <th className="px-2 py-1 text-right border border-stone-200 bg-blue-50/60">Vendor Cost</th>
                    <th className="px-2 py-1 text-right border border-stone-200 bg-blue-50/60">Client Revenue</th>
                    <th className="px-2 py-1 text-right border border-stone-200 bg-blue-50/60">Profit</th>
                    <th className="px-2 py-1 text-right border border-stone-200 bg-blue-50/60">Margin %</th>
                    <th className="px-2 py-1 text-right border border-stone-200 bg-emerald-50/60">Vendor Paid</th>
                    <th className="px-2 py-1 text-right border border-stone-200 bg-emerald-50/60">Client Revenue</th>
                    <th className="px-2 py-1 text-right border border-stone-200 bg-emerald-50/60">Profit</th>
                    <th className="px-2 py-1 text-right border border-stone-200 bg-emerald-50/60">Margin %</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const isOpen = !!expanded[r.category_code];
                    const payments = r.payments || [];
                    const paymentCount = payments.length;
                    return (
                    <Fragment key={r.category_code}>
                    <tr className="hover:bg-stone-50/60" data-testid={`profitability-row-${r.category_code}`}>
                      <td className="px-2 py-1.5 border border-stone-200 font-medium">
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 hover:text-emerald-800"
                          onClick={() => setExpanded((p) => ({ ...p, [r.category_code]: !p[r.category_code] }))}
                          data-testid={`profitability-expand-${r.category_code}`}
                          title={paymentCount ? 'Click to manage linked payments' : 'No vendor payments linked to this category'}
                          disabled={paymentCount === 0}
                        >
                          {paymentCount > 0 ? (isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />) : <span style={{ width: 12 }} />}
                          <span>{r.category_code} — {r.category_name}</span>
                        </button>
                      </td>
                      <td className="px-2 py-1.5 text-center border border-stone-200 text-stone-500">{r.item_count}</td>
                      <td className="px-2 py-1.5 text-right border border-stone-200">{fmtINR(r.est_vendor_cost)}</td>
                      <td className="px-2 py-1.5 text-right border border-stone-200">{fmtINR(r.est_client_revenue)}</td>
                      <td className={`px-2 py-1.5 text-right border border-stone-200 font-semibold ${r.est_profit < 0 ? 'text-red-700' : 'text-emerald-700'}`}>{fmtINR(r.est_profit)}</td>
                      <td className={`px-2 py-1.5 text-right border border-stone-200 ${r.est_margin_pct < 0 ? 'text-red-700' : 'text-stone-700'}`}>{r.est_margin_pct}%</td>
                      <td className="px-2 py-1.5 text-right border border-stone-200">
                        <button
                          type="button"
                          className={`underline-offset-2 ${paymentCount > 0 ? 'text-emerald-800 hover:underline cursor-pointer' : 'text-stone-700 cursor-default'}`}
                          onClick={() => paymentCount > 0 && setExpanded((p) => ({ ...p, [r.category_code]: !p[r.category_code] }))}
                          data-testid={`profitability-paid-toggle-${r.category_code}`}
                          disabled={paymentCount === 0}
                          title={paymentCount > 0 ? `${paymentCount} payment(s) — click to manage` : ''}
                        >
                          {fmtINR(r.act_vendor_paid || 0)}{paymentCount > 0 ? ` (${paymentCount})` : ''}
                        </button>
                      </td>
                      <td className="px-2 py-1.5 text-right border border-stone-200">{fmtINR(r.act_client_revenue)}</td>
                      <td className={`px-2 py-1.5 text-right border border-stone-200 font-semibold ${r.act_profit < 0 ? 'text-red-700' : 'text-emerald-700'}`}>{fmtINR(r.act_profit)}</td>
                      <td className={`px-2 py-1.5 text-right border border-stone-200 ${r.act_margin_pct < 0 ? 'text-red-700' : 'text-stone-700'}`}>{r.act_margin_pct}%</td>
                    </tr>
                    {isOpen && paymentCount > 0 && (
                      <tr data-testid={`profitability-payments-row-${r.category_code}`} className="bg-emerald-50/30">
                        <td colSpan={10} className="px-3 py-3 border border-stone-200">
                          <div className="text-[11px] font-semibold text-emerald-900 mb-1 flex items-center gap-1">
                            Linked vendor payments — amend amount, reassign, or delink
                          </div>
                          <table className="w-full text-xs">
                            <thead className="text-stone-600">
                              <tr className="border-b border-stone-200">
                                <th className="text-left py-1 px-1">Vendor</th>
                                <th className="text-left py-1 px-1">Date</th>
                                <th className="text-left py-1 px-1">Notes</th>
                                <th className="text-right py-1 px-1 w-36">Amount</th>
                                <th className="text-left py-1 px-1 w-44">Reassign to</th>
                                <th className="text-right py-1 px-1 w-32">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {payments.map((p) => {
                                const draftAmt = amountDraft[p.id];
                                const hasDraft = draftAmt !== undefined && parseFloat(draftAmt) !== p.amount;
                                const isSaving = !!savingPayment[p.id];
                                return (
                                  <tr key={p.id} className="border-b border-stone-100" data-testid={`profitability-payment-${p.id}`}>
                                    <td className="py-1 px-1 text-stone-700">{p.vendor_name || '—'}</td>
                                    <td className="py-1 px-1 text-stone-600">{p.payment_date ? new Date(p.payment_date).toLocaleDateString('en-IN') : '—'}</td>
                                    <td className="py-1 px-1 text-stone-500 truncate max-w-[180px]" title={p.notes}>{p.notes || '—'}</td>
                                    <td className="py-1 px-1 text-right">
                                      <input
                                        type="number"
                                        step="0.01"
                                        min="0.01"
                                        value={draftAmt !== undefined ? draftAmt : p.amount}
                                        onChange={(e) => setAmountDraft((d) => ({ ...d, [p.id]: e.target.value }))}
                                        className="w-full h-7 text-xs border border-stone-300 rounded px-1 text-right"
                                        data-testid={`profitability-amount-input-${p.id}`}
                                        disabled={isSaving}
                                      />
                                    </td>
                                    <td className="py-1 px-1">
                                      <select
                                        onChange={(e) => { const v = e.target.value; if (v && v !== r.category_code) reassignPayment(p.id, v); }}
                                        value={r.category_code}
                                        disabled={isSaving}
                                        className="w-full h-7 text-xs border border-stone-300 rounded px-1 bg-white"
                                        data-testid={`profitability-reassign-${p.id}`}
                                      >
                                        {(effectiveLegendCategories || []).map((c) => {
                                          const code = c.code || c.group;
                                          const name = c.name || c.group_name;
                                          return <option key={code} value={code}>{code} — {name}</option>;
                                        })}
                                      </select>
                                    </td>
                                    <td className="py-1 px-1 text-right">
                                      <div className="inline-flex items-center gap-1">
                                        <button
                                          type="button"
                                          onClick={() => amendAmount(p.id)}
                                          disabled={!hasDraft || isSaving}
                                          className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-[11px] ${hasDraft ? 'border-emerald-400 bg-emerald-100 text-emerald-800 hover:bg-emerald-200' : 'border-stone-200 bg-stone-50 text-stone-400 cursor-not-allowed'}`}
                                          data-testid={`profitability-save-${p.id}`}
                                          title="Save amended amount"
                                        >
                                          <Save size={11} /> Save
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => delinkPayment(p.id)}
                                          disabled={isSaving}
                                          className="inline-flex items-center gap-1 px-2 py-1 rounded border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 text-[11px]"
                                          data-testid={`profitability-delink-${p.id}`}
                                          title="Delink (move back to Uncategorized)"
                                        >
                                          <Unlink size={11} /> Delink
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                          <p className="text-[10px] text-stone-500 italic mt-1.5">
                            Tip: change the amount to fix typos (partial amend), pick a different category to reassign, or click <strong>Delink</strong> to send the payment back to the Uncategorized banner at the top of this dialog.
                          </p>
                        </td>
                      </tr>
                    )}
                    </Fragment>
                    );
                  })}
                  <tr className="bg-amber-50 font-bold" data-testid="profitability-total-row">
                    <td className="px-2 py-2 border border-stone-200">TOTAL</td>
                    <td className="px-2 py-2 text-center border border-stone-200">{rows.reduce((s, r) => s + r.item_count, 0)}</td>
                    <td className="px-2 py-2 text-right border border-stone-200">{fmtINR(totals.est_vendor_cost)}</td>
                    <td className="px-2 py-2 text-right border border-stone-200">{fmtINR(totals.est_client_revenue)}</td>
                    <td className={`px-2 py-2 text-right border border-stone-200 ${totals.est_profit < 0 ? 'text-red-700' : 'text-emerald-800'}`}>{fmtINR(totals.est_profit)}</td>
                    <td className="px-2 py-2 text-right border border-stone-200">{totals.est_margin_pct}%</td>
                    <td className="px-2 py-2 text-right border border-stone-200">{fmtINR(totals.act_vendor_paid)}</td>
                    <td className="px-2 py-2 text-right border border-stone-200">{fmtINR(totals.act_client_revenue)}</td>
                    <td className={`px-2 py-2 text-right border border-stone-200 ${totals.act_profit < 0 ? 'text-red-700' : 'text-emerald-800'}`}>{fmtINR(totals.act_profit)}</td>
                    <td className="px-2 py-2 text-right border border-stone-200">{totals.act_margin_pct}%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-stone-200 bg-white flex items-center justify-end gap-2 flex-shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="profitability-close-btn"><X size={14} className="mr-1" />Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
